import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUserStore } from '@/store/useUserStore';
import { useEffect } from 'react';
// import { Outlet } from 'react-router-dom';

/**
 * AuthGuard Component
 *
 * Prevents unauthenticated users from accessing protected routes.
 * Checks if the user is logged in and the token is still valid (not expired).
 * If the user is not logged in or the token is expired, redirects to login,
 * preserving the intended destination so we can return there after sign-in.
 * Fine-grained role/permission gating happens one level down in ProtectedRoute.
 */
export const AuthGuard = () => {
  const { token, user, logout } = useUserStore();
  const location = useLocation();

  // Auto-logout when the token's expiry passes (and immediately if already past).
  useEffect(() => {
    if (token && user) {
      // Check if token has an expiry (API returns expires_at)
      const tokenExpiry = user.expires_at;
      
      if (tokenExpiry) {
        const expiryTime = new Date(tokenExpiry).getTime();
        const currentTime = Date.now();
        
        // If token is expired, logout immediately
        if (currentTime > expiryTime) {
          console.log('Session expired. Logging out...');
          logout();
          return;
        } else {
          // Set a timer to auto-logout when token expires
          const timeUntilExpiry = expiryTime - currentTime;
          const timeoutId = setTimeout(() => {
            console.log('Session expired. Logging out...');
            logout();
          }, timeUntilExpiry);
          
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [token, user, logout]);

  // If no token or no user, redirect to login (remembering where they were headed).
  if (!token || !user) {
    const currentPath = location.pathname + location.search;
    const isJoinSessionPath = location.pathname.startsWith('/portal/join-session');
    const hasToken = location.search.includes('token=');
    
    let redirectPath = currentPath;
    if (isJoinSessionPath && !hasToken) {
      redirectPath = '/portal';
    }
    
    if (redirectPath && redirectPath !== '/' && redirectPath !== '/portal') {
      sessionStorage.setItem('redirectAfterLogin', redirectPath);
    }
    
    return <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
  }

  // Authenticated: hand off to the nested route (ProtectedRoute gates by role).
  return <Outlet />;
};

